import store from '../store/index'
function formatNumber(decimalCount, amount, symbol, thousands = ",", decimal = ".") {
    try {
        decimalCount = Math.abs(decimalCount);
        decimalCount = isNaN(decimalCount) ? 2 : decimalCount;

        const negativeSign = amount < 0 ? "-" : "";

        // Convert the amount to a fixed-point notation
        amount = Number(amount).toFixed(decimalCount);

        // Use regex to remove unnecessary trailing zeros and the decimal point if it is an integer
        let [integerPart, fractionalPart] = amount.split('.');

        // Trimming the fractional part of trailing zeros if necessary
        if (fractionalPart) {
            fractionalPart = fractionalPart.replace(/0+$/, '');
        }

        let i = Math.abs(parseInt(integerPart)).toString(); // Ensure the integer part is positive for the split
        let j = i.length > 3 ? i.length % 3 : 0;

        return (
            symbol +
            ' ' +
            negativeSign +
            (j ? i.substr(0, j) + thousands : '') +
            i.substr(j).replace(/(\d{3})(?=\d)/g, '$1' + thousands) +
            (fractionalPart ? decimal + fractionalPart : '')
        );
    } catch (e) {
        console.error(e);
    }
}

export const utils = {
    toPascalCase(string) {
        return string
            .replace(/\w\S*/g, txt => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase())
            .replace(/^\w/, s => s.toUpperCase());
    },

    cutLength(string, limit = 45) {
        if (string.length < limit) {
            return string
        } else {
            return string.toString().substring(0, limit).concat('...')
        }
    },
    /**
     * Rounds a value to a fixed number of decimal places using true
     * round-half-away-from-zero semantics, returning a Number (not a String).
     *
     * Implementation note: a naive `Math.round(n * factor) / factor` is
     * broken by IEEE-754 representation. E.g. `1.005` is stored as
     * `1.00499999...`, so `Math.round(1.005 * 100) / 100` returns `1` instead
     * of `1.01`. `Number.EPSILON` is too small to nudge such values across
     * the rounding boundary. The standard, dependency-free fix is the
     * string-exponent trick on the way up — `Number(n + 'e' + d)` is
     * parsed by the JS engine from the decimal string and lands on the
     * nearest representable integer — then a plain numeric divide on the
     * way back down (the divide is exact because the numerator is the
     * already-rounded integer, so no IEEE-754 ambiguity remains).
     *
     * `Math.round` rounds halves toward +Infinity, so negative halves like
     * `-0.00005` at 4 dp round to `-0.0001` (the divide keeps the sign).
     *
     * - Coerces non-numeric input to `0` so callers do not have to null-check.
     * - Negative or non-integer `decimals` is clamped to `0`.
     *
     * Used everywhere stock / sale-line quantities are normalised to 4 dp
     * before they are stored, displayed, or sent to the server.
     *
     * @param {number|string|null|undefined} value Value to round.
     * @param {number} decimals Number of decimal places to keep (default 4).
     * @returns {number} The rounded value, or `0` when input is not finite.
     */
    roundTo(value, decimals = 4) {
        let n = Number(value);
        if (!Number.isFinite(n)) return 0;
        if (!Number.isInteger(decimals) || decimals < 0) decimals = 0;
        if (decimals === 0) return Math.round(n);
        // The string-exponent dodge for halves like 1.005, 2.675, 0.125.
        // Going *up* via the decimal string is what fixes the float trap;
        // going *back down* by plain division is exact because the
        // numerator is already an integer in the 4-decimal scale.
        //
        // For values that JS would render in scientific notation even
        // before the trick (e.g. 0.0000001 -> "1e-7"), the string trick
        // produces a malformed "1e-7e4" and yields NaN. For those cases
        // fall back to plain multiplication, which is safe because the
        // shifted value is < 1 and therefore rounds to 0 anyway (no
        // half-boundary to disambiguate).
        let usesScientific = Math.abs(n) < 1e-6;
        let scaled;
        if (usesScientific) {
            scaled = n * Math.pow(10, decimals);
        } else {
            scaled = Number(n + 'e' + decimals);
        }
        // Math.round rounds halves toward +Infinity, so for negative halves
        // (e.g. -0.5) it returns -0, not -1. Sign-aware rounding fixes that.
        let rounded = scaled >= 0
            ? Math.round(scaled)
            : -Math.round(-scaled);
        return rounded / Math.pow(10, decimals);
    },
    /**
     * Formats an amount using the store's base currency symbol, or without a symbol when isCurrency is false.
     * Callers that need a different currency symbol must use formatCurrency with an explicit currency argument.
     * @param {number|string} amount - Amount to format.
     * @param {number} decimalCount - Maximum number of decimal places.
     * @param {string} decimal - Decimal separator.
     * @param {string} thousands - Thousands separator.
     * @param {boolean} isCurrency - Whether to include the base currency symbol.
     * @returns {string|undefined} The formatted amount.
     */
    formatMoney(amount, decimalCount = 2, decimal = ".", thousands = ",",isCurrency = true) {
        if (!isCurrency){
            return formatNumber(decimalCount, amount, '', thousands, decimal);
        }

        let currency =store.getters.getBaseCurrency.Symbol;
        return formatNumber(decimalCount, amount, currency, thousands, decimal);
    },
    /**
     * Formats a quantity for display. The underlying value is first rounded
     * to 4 decimal places (the canonical sale-line precision), then rendered
     * with `formatMoney` which trims trailing zeros — so 0.1000 displays as
     * "0.1", 0.1230 as "0.123", 0.1234 as "0.1234", 1.0000 as "1".
     *
     * @param {number|string} amount Raw quantity.
     * @returns {string} The display string.
     */
    formatQTY(amount) {
        let rounded = this.roundTo(amount, 4);
        return this.formatMoney(rounded, 4, '.', ',', false);
    },
    /**
     * Formats an amount using the supplied currency symbol, defaulting to the store's base currency.
     * Callers that need a non-base symbol must pass it explicitly through the currency argument.
     * @param {number|string} amount - Amount to format.
     * @param {number} decimalCount - Maximum number of decimal places.
     * @param {string} currency - Currency symbol to display.
     * @returns {string|undefined} The formatted currency amount.
     */
    formatCurrency(amount, decimalCount = 2,currency = store.getters.getBaseCurrency.Symbol) {
        return formatNumber(decimalCount, amount, currency);
    },
    removeIf(arr, predicate) {
        return arr.filter(predicate);
    },
    remove(arr, obj) {
        return arr.filter(v => v !== obj)
    },
    showLoading: () => store.commit(`setLoadingState`, true),
    hideLoading: () => store.commit(`setLoadingState`, false),
}
